import React, { type ComponentProps } from 'react';
import { AdvancedImage, responsive, placeholder } from '@cloudinary/react';
import { dpr } from '@cloudinary/url-gen/actions/delivery';
import { cloudinary } from '../shared/cloudinary';

type CloudinaryPlugin = NonNullable<ComponentProps<typeof AdvancedImage>['plugins']>[number];

/**
 * Asks Cloudinary for the image at the screen's pixel ratio, so a 300px slot loads 600 real pixels
 * on a retina screen. Plugins run only in the browser, after mount, so server HTML stays the same.
 */
const retina: CloudinaryPlugin = async (_element, image) => {
  image.delivery(dpr(Math.min(3, Math.ceil(window.devicePixelRatio || 1))));
};

export type CloudImageProps = {
  cloudId?: string;

  /**
   * Width steps for the responsive plugin. A number rounds the slot width up to the next multiple.
   * An array picks the closest listed width, which can be smaller than the slot.
   */
  steps?: number | number[];
  usePlaceholder?: boolean;
  alt?: string;
  style?: React.CSSProperties;
};

export default function CloudImage({
  cloudId: src,
  steps = 100,
  usePlaceholder = false,
  alt,
  style = { width: '100%', height: '100%' },
}: CloudImageProps) {
  'use memo';

  const image = cloudinary.image(src);
  const plugins = [retina, responsive({ steps })];
  if (usePlaceholder) {
    plugins.push(placeholder());
  }

  return (
    <AdvancedImage
      className="cloud-image"
      cldImg={image}
      style={style}
      plugins={plugins}
      alt={alt}
    />
  );
}
